import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Mail,
  Phone,
  MapPin,
  Linkedin,
  Twitter,
  Facebook,
  Youtube,
} from "lucide-react";
import { useContactData } from "../hooks/useContactData";
import { BrandLogo } from "./BrandLogo";
import { listLegalPages } from "../services/legalService";

const FALLBACK_LEGAL_LINKS = [
  { label: "Privacy Policy", path: "/privacy-policy" },
  { label: "Terms of Service", path: "/terms-of-service" },
  { label: "Refund Policy", path: "/refund-policy" },
  { label: "Business Policy", path: "/business-policy" },
  { label: "Cookie Policy", path: "/cookie-policy" },
];

export const Footer = () => {
  const contactData = useContactData();
  const [legalLinks, setLegalLinks] = useState(FALLBACK_LEGAL_LINKS);

  useEffect(() => {
    let cancelled = false;
    listLegalPages().then((pages) => {
      if (cancelled || !Array.isArray(pages) || !pages.length) return;
      setLegalLinks(
        pages.map((page) => ({
          label: page.title,
          path: `/${page.slug}`,
        }))
      );
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const socialIcons = {
    linkedin: <Linkedin className="w-4 h-4" />,
    twitter: <Twitter className="w-4 h-4" />,
    facebook: <Facebook className="w-4 h-4" />,
    youtube: <Youtube className="w-4 h-4" />,
  };

  return (
    <footer className="relative bg-transparent text-text-sub pt-10 pb-8 overflow-hidden">
      <div className="absolute top-0 right-1/4 w-[500px] h-[500px] rounded-full bg-primary-600/5 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] rounded-full bg-cyan-500/5 blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="rounded-[1.75rem] border border-border bg-surface/80 backdrop-blur-md shadow-xl shadow-primary-600/5 p-8 sm:p-10 dark:bg-slate-950/60">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 pb-12 border-b border-border dark:border-slate-800">
          {/* Brand & Socials */}
          <div className="space-y-6">
            <BrandLogo size="sm" />
            <p className="text-sm leading-relaxed text-text-sub font-medium">
              The premier cloud ERP & CRM engineered exclusively for CAs, tax
              practitioners, and corporate governance firms. Automate operations
              and secure your practice.
            </p>
            {/* Socials */}
            <div className="flex items-center space-x-3">
              {Object.entries(contactData.socialLinks).map(([platform, url]) => {
                const icon = socialIcons[platform];
                if (!icon || !url) return null;
                return (
                  <a
                    key={platform}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center w-9 h-9 rounded-xl bg-muted hover:bg-gradient-to-br hover:from-primary-600 hover:to-cyan-500 hover:text-white transition-all duration-300 text-text-sub hover:-translate-y-0.5 border border-border/50"
                    aria-label={`Follow us on ${platform}`}
                  >
                    {icon}
                    <span className="sr-only">Follow us on {platform}</span>
                  </a>
                );
              })}
            </div>
          </div>

          {/* Quick Navigation Links */}
          <div>
            <h3 className="font-heading font-bold text-xs tracking-widest text-foreground uppercase mb-6">
              Navigation
            </h3>
            <ul className="space-y-3.5">
              {[
                { label: "Home Overview", path: "/" },
                { label: "Services Catalog", path: "/services" },
                { label: "How It Works", path: "/how-it-works" },
                { label: "Industries Served", path: "/industries" },
                { label: "Pricing Plans", path: "/pricing" },
                { label: "Security & Trust", path: "/security" },
                { label: "FAQ", path: "/faq" },
                { label: "Resources", path: "/resources" },
                { label: "About", path: "/about" },
                { label: "Support & Contact", path: "/contact" },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm font-medium hover:text-primary-600 transition-colors duration-200"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Quick View */}
          <div>
            <h3 className="font-heading font-bold text-xs tracking-widest text-foreground uppercase mb-6">
              Core Modules
            </h3>
            <ul className="space-y-3.5">
              {contactData.services.slice(0, 5).map((service) => (
                <li key={service.id}>
                  <Link
                    to={`/services/${service.id}`}
                    className="text-sm font-medium hover:text-primary-600 transition-colors duration-200 block truncate"
                  >
                    {service.title.replace(" (CRM)", "").replace(" System", "")}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact & Hours */}
          <div className="space-y-6">
            <h3 className="font-heading font-bold text-xs tracking-widest text-foreground uppercase mb-4">
              Get in Touch
            </h3>
            <ul className="space-y-3.5 text-sm font-medium">
              <li className="flex items-start space-x-2.5">
                <MapPin className="w-4 h-4 text-primary-600 shrink-0 mt-1" />
                <span className="leading-relaxed">{contactData.primaryAddress}</span>
              </li>
              <li className="flex items-center space-x-2.5">
                <Mail className="w-4 h-4 text-primary-600 shrink-0" />
                <a
                  href={`mailto:${contactData.supportEmail}`}
                  className="hover:text-primary-600 transition-colors"
                >
                  {contactData.supportEmail}
                </a>
              </li>
              <li className="flex items-center space-x-2.5">
                <Phone className="w-4 h-4 text-primary-600 shrink-0" />
                <a
                  href={`tel:${contactData.phone?.[0]?.phone?.replace(/\s+/g, "") || ""}`}
                  className="hover:text-primary-600 transition-colors"
                >
                  {contactData.phone?.[0]?.phone}
                </a>
              </li>
            </ul>

            {/* Newsletter / updates CTA */}
            <div className="pt-2 space-y-3">
              <h4 className="font-heading font-bold text-[10px] text-foreground uppercase tracking-wider">
                Regulatory Updates Newsletter
              </h4>
              <p className="text-sm leading-relaxed text-text-sub font-medium">
                Stay updated through our support email and product announcements
                when new compliance features roll out.
              </p>
            </div>
          </div>
        </div>

        {/* Footer Bottom (Legal + Certifications + Copyright) */}
        <div className="pt-8 flex flex-col gap-6">
          <div className="flex flex-wrap items-center justify-center md:justify-start gap-x-4 gap-y-2">
            {legalLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="text-xs font-semibold text-text-sub hover:text-primary-600 transition-colors"
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/account-deletion"
              className="text-xs font-semibold text-text-sub hover:text-primary-600 transition-colors"
            >
              Account Deletion
            </Link>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <p className="text-xs font-medium text-text-sub">
              &copy; {new Date().getFullYear()} {contactData.companyName}. All
              rights reserved.
            </p>

            {/* Certifications Badge items */}
            <div className="flex flex-wrap items-center justify-center gap-3 text-[9px] uppercase font-black tracking-[0.15em] text-text-sub">
              <span className="px-3 py-1.5 rounded-lg border border-border bg-muted/50">
                ISO 27001 Certified
              </span>
              <span className="px-3 py-1.5 rounded-lg border border-border bg-muted/50">
                GDPR Data Compliant
              </span>
              <span className="px-3 py-1.5 rounded-lg border border-border bg-muted/50">
                256-Bit SSL Encrypted
              </span>
            </div>
          </div>
        </div>
        </div>
      </div>
    </footer>
  );
};
